import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme, lighten, Grid, TextField, Button, FormControl, InputLabel, Select, SelectChangeEvent, MenuItem, Typography } from "@mui/material";
import { useIntl } from "react-intl";

import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";

import TitledCard from "../TitledCard";
import { TemplateListItemProps } from "../types";
import { Zone } from "./types";
import useEditedStyles, { useEditedColor } from "../../../../hooks/useEditedStyles";
import { hexInputCheck, valueEdited } from "../utils";
import { useThermowatch } from "./ThermowatchTemplate";

interface ZoneItemProps extends TemplateListItemProps<Zone> {
  defaultZone: string;
  defaultType: string;
  defaultReport: string;
  defaultAlarm: string;
}

const ZoneItem = ({ index, value, saved, onChange, onDelete, onRestore, disabled, defaultZone, defaultType, defaultReport, defaultAlarm }: ZoneItemProps) => {
  const [name, setName] = useState(value.name || "");
  const [zone, setZone] = useState(value.zone || "");
  const [type, setType] = useState(value.type || "");
  const [address, setAddress] = useState(value.address || "");
  const [report, setReport] = useState(value.report || "");
  const [alarm, setAlarm] = useState(value.alarm || "");

  const { reports, alarms } = useThermowatch();
  const { formatMessage } = useIntl();
  const editedStyle = useEditedStyles();
  const { background: editedBackground, text: editedText } = useEditedColor();
  const theme = useTheme();

  const handleReportChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      const v = e.target.value === "default" ? "" : e.target.value;
      setReport(v);
      if (v) {
        value.report = v;
      } else {
        delete value.report;
      }

      onChange(index, value);
    },
    [value, index, onChange]
  );

  const handleAlarmChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      const v = e.target.value === "default" ? "" : e.target.value;
      setAlarm(v);
      if (v) {
        value.alarm = v;
      } else {
        delete value.alarm;
      }

      onChange(index, value);
    },
    [value, index, onChange]
  );

  useEffect(() => {
    setName(value.name || "");
    setZone(value.zone || "");
    setType(value.type || "");
    setAddress(value.address || "");
    setReport(value.report || "");
    setAlarm(value.alarm || "");
  }, [value]);

  const defaultReportItem = useMemo(() => {
    if (index === 0) {
      return null;
    }
    const i = reports.findIndex((r) => r.uuid === defaultReport);
    const r = reports[i];
    return (
      <MenuItem value="default">
        <Typography variant="body2" sx={{ fontStyle: "italic" }}>
          {`${formatMessage(
            { defaultMessage: "{name, select, null {Report #{index}} other {{name}}}" },
            { name: (r ? r.name : formatMessage({ defaultMessage: "No report" })) || null, index: !r || r.name ? null : i + 1 }
          )} (${formatMessage({ defaultMessage: "inherited" })})`}
        </Typography>
      </MenuItem>
    );
  }, [index, reports, defaultReport, formatMessage]);

  const defaultAlarmItem = useMemo(() => {
    if (index === 0) {
      return null;
    }
    const i = alarms.findIndex((r) => r.uuid === defaultAlarm);
    const r = alarms[i];
    return (
      <MenuItem value="default">
        <Typography variant="body2" sx={{ fontStyle: "italic" }}>
          {`${formatMessage(
            { defaultMessage: "{name, select, null {Alarm #{index}} other {{name}}}" },
            { name: (r ? r.name : formatMessage({ defaultMessage: "No alarm" })) || null, index: !r || r.name ? null : i + 1 }
          )} (${formatMessage({ defaultMessage: "inherited" })})`}
        </Typography>
      </MenuItem>
    );
  }, [index, alarms, defaultAlarm, formatMessage]);

  return (
    <TitledCard
      title={
        (value.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} | ` : "") +
        formatMessage(
          { defaultMessage: "Zone{name, select, null {{nr, select, null {} other { #{nr}}}} other {: {name}}}" },
          { name: name || null, nr: name || !zone ? null : parseInt(zone, 16).toString(16).toUpperCase() }
        )
      }
      color={value.deleted ? editedBackground : disabled ? lighten(theme.palette.primary.main, 0.5) : theme.palette.primary.main}
      contrastColor={value.deleted ? editedText : theme.palette.secondary.contrastText}
    >
      <Grid container spacing={1} padding={1} flexDirection="row-reverse" alignContent="flex-start" justifyContent="space-between">
        <Grid item xs={12} sm="auto">
          {!value.deleted && !disabled && (
            <Button startIcon={<DeleteForeverOutlinedIcon />} color="error" onClick={() => onDelete(index)} sx={{ float: { xs: "right", sm: "inherit" } }}>
              {formatMessage({ defaultMessage: "Remove" })}
            </Button>
          )}
          {value.deleted && !disabled && (
            <Button color="inherit" onClick={() => onRestore(index)}>
              {formatMessage({ defaultMessage: "Restore" })}
            </Button>
          )}
        </Grid>
        <Grid item alignSelf="flex-start" flex={1} container spacing={1}>
          <Grid item xs={12} sm="auto">
            <TextField
              label={formatMessage({ defaultMessage: "Name" })}
              variant="filled"
              value={name}
              className={valueEdited(saved?.name, name, value.name) ? editedStyle.input : ""}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => {
                if (name) {
                  value.name = name;
                } else {
                  delete value.name;
                }
                onChange(index, value);
              }}
              size="small"
              autoComplete="off"
              sx={{ margin: 0, minWidth: { sm: 220 }, width: { sm: 220 } }}
              disabled={disabled || value.deleted}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField
              label={formatMessage({ defaultMessage: "Zone number" })}
              placeholder={
                defaultZone
                  ? `${defaultZone} (${index === 0 ? formatMessage({ defaultMessage: "default" }) : formatMessage({ defaultMessage: "calculated" })})`
                  : undefined
              }
              InputLabelProps={{ shrink: true }}
              variant="filled"
              value={zone}
              className={valueEdited(saved?.zone, zone, value.zone) ? editedStyle.input : ""}
              inputProps={{ maxLength: 3, style: zone ? { textTransform: "uppercase" } : { fontStyle: "italic" } }}
              onChange={(e) => hexInputCheck(e.target.value, setZone)}
              onBlur={() => {
                if (zone) {
                  value.zone = zone.padStart(3, "0");
                } else {
                  delete value.zone;
                }
                onChange(index, value);
              }}
              sx={{ margin: 0, minWidth: { sm: 160 }, width: { sm: 160 } }}
              size="small"
              autoComplete="off"
              disabled={disabled || value.deleted}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField
              label={formatMessage({ defaultMessage: "Type" })}
              placeholder={
                defaultType
                  ? `${defaultType} (${index === 0 ? formatMessage({ defaultMessage: "default" }) : formatMessage({ defaultMessage: "inherited" })})`
                  : undefined
              }
              InputLabelProps={{ shrink: true }}
              variant="filled"
              value={type}
              className={valueEdited(saved?.type, type, value.type) ? editedStyle.input : ""}
              onChange={(e) => setType(e.target.value)}
              onBlur={() => {
                if (type) {
                  value.type = type;
                } else {
                  delete value.type;
                }
                onChange(index, value);
              }}
              sx={{ margin: 0, minWidth: { sm: 180 }, width: { sm: 180 } }}
              size="small"
              autoComplete="off"
              disabled={disabled || value.deleted}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField
              required
              label={formatMessage({ defaultMessage: "Address" })}
              variant="filled"
              value={address}
              className={valueEdited(saved?.address, address, value.address) ? editedStyle.input : ""}
              // inputProps={{ style: { textTransform: "uppercase" } }}
              onChange={(e) => setAddress(e.target.value)}
              onBlur={() => {
                value.address = address;
                onChange(index, value);
              }}
              sx={{ margin: 0, minWidth: { sm: 220 }, width: { sm: 220 } }}
              size="small"
              autoComplete="off"
              disabled={disabled || value.deleted}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <FormControl
              variant="filled"
              size="small"
              sx={{ margin: 0, minWidth: { sm: 220 }, width: { sm: 220 } }}
              className={
                valueEdited(saved?.report, report, value.report) ||
                reports
                  ?.filter((r) => r.deleted)
                  .map((r) => r.uuid)
                  .includes(report)
                  ? editedStyle.input
                  : ""
              }
              disabled={disabled || value.deleted || reports.length === 0}
              fullWidth
            >
              <InputLabel id="report-select-label">{formatMessage({ defaultMessage: "Report" })}</InputLabel>
              <Select labelId="report-select-label" value={report || "default"} onChange={handleReportChange}>
                {defaultReportItem}
                <MenuItem value="-1" sx={{ fontVariant: "small-caps", textTransform: "lowercase" }}>
                  {formatMessage({ defaultMessage: "No report" })}
                </MenuItem>
                {reports?.map((report, i) => (
                  <MenuItem key={`report-${report.uuid}`} value={report.uuid} className={report.deleted ? editedStyle.withHover : ""}>
                    {[
                      report.deleted && `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |`,
                      formatMessage(
                        { defaultMessage: "{name, select, null {Report #{index}} other {{name}}}" },
                        { name: report.name || null, index: report.name ? null : i + 1 }
                      ),
                    ]
                      .filter((i) => !!i)
                      .join(" ")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto">
            <FormControl
              variant="filled"
              size="small"
              sx={{ margin: 0, minWidth: { sm: 220 }, width: { sm: 220 } }}
              className={
                valueEdited(saved?.alarm, alarm, value.alarm) ||
                alarms
                  ?.filter((a) => a.deleted)
                  .map((a) => a.uuid)
                  .includes(alarm)
                  ? editedStyle.input
                  : ""
              }
              disabled={disabled || value.deleted || alarms.length === 0}
              fullWidth
            >
              <InputLabel id="alarm-select-label">{formatMessage({ defaultMessage: "Alarm" })}</InputLabel>
              <Select labelId="alarm-select-label" value={alarm || "default"} onChange={handleAlarmChange}>
                {defaultAlarmItem}
                <MenuItem value="-1" sx={{ fontVariant: "small-caps", textTransform: "lowercase" }}>
                  {formatMessage({ defaultMessage: "No alarm" })}
                </MenuItem>
                {alarms?.map((alarm, i) => (
                  <MenuItem key={`alarm-${alarm.uuid}`} value={alarm.uuid} className={alarm.deleted ? editedStyle.withHover : ""}>
                    {[
                      alarm.deleted && `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |`,
                      formatMessage(
                        { defaultMessage: "{name, select, null {Alarm #{index}} other {{name}}}" },
                        { name: alarm.name || null, index: alarm.name ? null : i + 1 }
                      ),
                    ]
                      .filter((i) => !!i)
                      .join(" ")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
    </TitledCard>
  );
};

export default ZoneItem;
